import React, { useState, useContext, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TemplateDataContext from "../../../Contexts/TemplateData.context";
import FormDataContext from "../../../Contexts/FormData.context";
import { jsonKeyMapping, templateActionEnum } from "../../../Config/Constants";
import { TemplateObject } from "../../../Models/TemplateObject";
import {
  isStepCompleted,
  hasIncompleteFields,
} from "../../../Util/StepCompletionHelper";
import MissingInfoModal from "../TemplateDesign/MissingInfoModal";

type Props = {
  uri: string;
  isHideSteps: string[];
};

const HorizontalNavigation: React.FC<Props> = ({ uri, isHideSteps }) => {
  // Render only on mobile / tablet widths (< 992px)
  const MOBILE_BREAKPOINT = 992;
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : true
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobileViewport(mobile);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentStepData, setCurrentStepData] = useState<TemplateObject | null>(
    null
  );
  const [availableSteps, setAvailableSteps] = useState<TemplateObject[]>([]);
  const [missingInfoModalOpen, setMissingInfoModalOpen] = useState(false);
  const [initialMissingStepSlug, setInitialMissingStepSlug] = useState<string | undefined>(undefined);

  const { state, dispatch } = useContext(TemplateDataContext);
  const { activeTemplateGroupBy, activeTemplateSteps } = state;
  const { state: formState } = useContext(FormDataContext);
  const { formData } = formState;

  const { stepName } = useParams();
  const navigate = useNavigate();

  // Get current step and available steps
  useEffect(() => {
    if (activeTemplateSteps.length > 0 && stepName) {
      const current = activeTemplateSteps.find(
        (step: TemplateObject) => step[jsonKeyMapping.SLUGIFY] === stepName
      );
      setCurrentStepData(current || activeTemplateSteps[0]);

      const steps: TemplateObject[] = [];
      activeTemplateGroupBy.forEach((item: any) => {
        if (item.groupName !== undefined) {
          const groupSteps = item[item.groupName] || [];
          groupSteps.forEach((step: TemplateObject) => {
            if (!isHideSteps.includes(step.slugify)) {
              const hasFormGroup =
                step[jsonKeyMapping.FORM_GROUP] &&
                Array.isArray(step[jsonKeyMapping.FORM_GROUP]) &&
                step[jsonKeyMapping.FORM_GROUP].length > 0;
              const isReviewStep =
                step[jsonKeyMapping.SLUGIFY] === "review-submit";
              // Include normal steps that have fields AND also the review step (even though it has none)
              if (hasFormGroup || isReviewStep) {
                steps.push(step);
              }
            }
          });
        } else {
          if (!isHideSteps.includes(item.slugify)) {
            const hasFormGroup =
              item[jsonKeyMapping.FORM_GROUP] &&
              Array.isArray(item[jsonKeyMapping.FORM_GROUP]) &&
              item[jsonKeyMapping.FORM_GROUP].length > 0;
            const isReviewStep =
              item[jsonKeyMapping.SLUGIFY] === "review-submit";
            if (hasFormGroup || isReviewStep) {
              steps.push(item);
            }
          }
        }
      });

      setAvailableSteps(steps);
    }
  }, [activeTemplateSteps, activeTemplateGroupBy, stepName, isHideSteps]);

  // --- KEY CHANGE: useEffect to manage the body class ---
  useEffect(() => {
    if (isDropdownOpen) {
      document.body.classList.add("steps-dropdown-open");
      // Optional: Add a class to disable body scroll when dropdown is open
      // document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove("steps-dropdown-open");
      // document.body.style.overflow = ''; // Restore body scroll
    }

    // Cleanup function: important to remove the class if the component unmounts
    return () => {
      document.body.classList.remove("steps-dropdown-open");
      // document.body.style.overflow = '';
    };
  }, [isDropdownOpen]); // This effect runs whenever isDropdownOpen state changes

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const stepsWithFields = useMemo(() => {
    return (activeTemplateSteps || []).filter(
      (s: any) =>
        s &&
        s[jsonKeyMapping.FORM_GROUP] &&
        Array.isArray(s[jsonKeyMapping.FORM_GROUP]) &&
        s[jsonKeyMapping.FORM_GROUP].length > 0 &&
        s[jsonKeyMapping.SLUGIFY] !== "review-submit"
    );
  }, [activeTemplateSteps]);

  const allStepsCompleted = useMemo(() => {
    if (!stepsWithFields || stepsWithFields.length === 0) return false;
    return stepsWithFields.every((s: any) => isStepCompleted(s, formData));
  }, [stepsWithFields, formData]);

  const shouldInterceptNavigation = stepName === 'review-submit' && !allStepsCompleted;

  const handleStepNavigation = (step: TemplateObject) => {
    const stepShowsIncompleteIcon = hasIncompleteFields(step, formData) && !isStepCompleted(step, formData);
    if (shouldInterceptNavigation && stepShowsIncompleteIcon) {
      setInitialMissingStepSlug(step[jsonKeyMapping.SLUGIFY]);
      setMissingInfoModalOpen(true);
      return;
    }
    navigate(`${uri}/${step[jsonKeyMapping.SLUGIFY]}`);
    setIsDropdownOpen(false);
    dispatch({ type: templateActionEnum.SET_NAVBAR_STATE, payload: false });
  };

  const getStepStatus = (step: TemplateObject) => {
    const currentStepIndex = activeTemplateSteps.findIndex(
      (s) => s[jsonKeyMapping.SLUGIFY] === stepName
    );

    const stepIndex = activeTemplateSteps.findIndex(
      (s) => s[jsonKeyMapping.SLUGIFY] === step[jsonKeyMapping.SLUGIFY]
    );

    if (step[jsonKeyMapping.SLUGIFY] === stepName) {
      return "current";
    }

    if (stepIndex < currentStepIndex) {
      const completed = isStepCompleted(step, formData);
      return completed ? "completed" : "upcoming"; // Keep "upcoming" for uncompleted previous steps
    }

    return "upcoming";
  };

  const renderStatusIcon = (step: TemplateObject, status: string) => {
    const stepCompleted = isStepCompleted(step, formData);
    const stepIncomplete = hasIncompleteFields(step, formData);

    if (stepCompleted) {
      return (
        <div className="dropdown-step-icon">
          <img
            src={`${process.env.PUBLIC_URL}/static/images/check-step.svg`}
            alt="Completed"
            className="icon-img"
          />
        </div>
      );
    }

    // Show incomplete icon only if it's the current step or a previous uncompleted step
    if (
      status === "current" ||
      (status === "upcoming" && !stepCompleted && stepIncomplete)
    ) {
      return (
        <div className="dropdown-step-icon">
          <img
            src={`${process.env.PUBLIC_URL}/static/images/text.svg`} // Assuming text.svg is your incomplete indicator
            alt="Incomplete"
            className="icon-img"
          />
        </div>
      );
    }

    // Default empty icon container for upcoming steps that are not current or explicitly incomplete
    return <div className="dropdown-step-icon"></div>;
  };

  if (!isMobileViewport) {
    // Ensure body class is cleaned if component stops rendering due to resize
    document.body.classList.remove("steps-dropdown-open");
    return null;
  }

  if (!currentStepData || availableSteps.length === 0) {
    return null;
  }

  return (
    <div className="horizontal-nav-container">
      {missingInfoModalOpen && (
        <MissingInfoModal
          open={missingInfoModalOpen}
          onClose={() => {
            setMissingInfoModalOpen(false);
            setInitialMissingStepSlug(undefined);
          }}
          initialStepSlug={initialMissingStepSlug}
        />
      )}
      {/* Current Step Bar - 263px width with hamburger */}
      <div
        className={`current-step-bar ${
          isDropdownOpen ? "dropdown-active" : "" // Add class to the bar itself
        }`}
      >
        <span className="current-step-text">
          {currentStepData[jsonKeyMapping.NAV_TITLE] ||
            currentStepData[jsonKeyMapping.SLUG]}
        </span>
        <button
          id="menuButton"
          className={`menu-button ${isDropdownOpen ? "active" : ""}`}
          onClick={toggleDropdown}
          aria-label="Toggle navigation menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 12H20M4 6H20M4 18H20"
              stroke="#16283D"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Steps Dropdown - 132px height, original vertical style */}
      <div className={`steps-dropdown ${isDropdownOpen ? "active" : ""}`}>
        {availableSteps.map((step: TemplateObject, index: number) => {
          const status = getStepStatus(step);
          return (
            <div
              key={index}
              className={`dropdown-step-item ${status}`}
              onClick={() => handleStepNavigation(step)}
            >
              <div className="dropdown-step-text">
                {step[jsonKeyMapping.NAV_TITLE] || step[jsonKeyMapping.SLUG]}
              </div>
              <div className="dropdown-step-spacer"></div>
              {renderStatusIcon(step, status)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HorizontalNavigation;
